import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';

const GainsHistoryScreen = ({ navigation }) => {
  const [weeklyHistory, setWeeklyHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, []);
  

  useEffect(() => {
    if (!auth.currentUser) {
      console.error('Usuário não autenticado');
      return;
    }

    const fetchWeeklyHistory = async () => {
      try {
        const userId = auth.currentUser.uid;
        const { startOfWeek, endOfWeek } = calculateWeekRange(new Date());
    
        await updateWeeklyData(userId, startOfWeek, endOfWeek);
    
        const weeklyQuery = query(
          collection(db, 'weeklyGains'),
          where('companyId', '==', userId)
        );
    
        const snapshot = await getDocs(weeklyQuery);
        const historyData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          startDate: doc.data().startDate ? new Date(doc.data().startDate.seconds * 1000) : null,
          endDate: doc.data().endDate ? new Date(doc.data().endDate.seconds * 1000) : null,
        }));
    
        // 🔥 Agora garantimos que a mais recente fica primeiro
        const sortedHistory = historyData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    
        setWeeklyHistory(sortedHistory);
      } catch (error) {
        console.error('Erro ao carregar o histórico semanal: ', error);
      } finally {
        setLoading(false);
      }
    };
    

    fetchWeeklyHistory();
  }, []);

  const calculateWeekRange = (date) => {
    const dayOfWeek = date.getDay();
    const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
  };

  const updateWeeklyData = async (userId, startOfWeek, endOfWeek) => {
    const weekDocId = `${userId}_${startOfWeek.toISOString().split('T')[0]}`;
    const weekDocRef = doc(db, 'weeklyGains', weekDocId);

    const ordersQuery = query(
      collection(db, 'pedido'),
      where('companyId', '==', userId),
      where('status', '==', 'completed')
    );

    const routesQuery = query(
      collection(db, 'routes'),
      where('companyId', '==', userId),
      where('status', '==', 'completed')
    );

    try {
      let sectorTotal = 0,
        neighborhoodTotal = 0,
        extraTotal = 0,
        routeTotal = 0,
        completedOrdersCount = 0;

      // Processar dados de pedidos
      const ordersSnapshot = await getDocs(ordersQuery);
      ordersSnapshot.forEach((doc) => {
        const data = doc.data();
        const completedAt = data.completedAt ? new Date(data.completedAt.seconds * 1000) : null;

        if (completedAt >= startOfWeek && completedAt <= endOfWeek) {
          completedOrdersCount++;
          if (data.locationType === 'Setor') sectorTotal += parseFloat(data.locationValue || 0);
          if (data.locationType === 'Bairro') neighborhoodTotal += parseFloat(data.locationValue || 0);
          if (data.locationType === 'Extra') extraTotal += parseFloat(data.locationValue || 0);
        }
      });

      // Processar dados de rotas
      const routesSnapshot = await getDocs(routesQuery);
      routesSnapshot.forEach((doc) => {
        const routeData = doc.data();
        const completedAt = routeData.completedAt
          ? new Date(routeData.completedAt.seconds * 1000)
          : null;

        if (completedAt >= startOfWeek && completedAt <= endOfWeek) {
          routeTotal += parseFloat(routeData.totalValue || 0);
        }
      });

      const totalGain = sectorTotal + neighborhoodTotal + extraTotal + routeTotal;

      // Salvar ou atualizar o histórico semanal
      await setDoc(
        weekDocRef,
        {
          companyId: userId,
          startDate: startOfWeek,
          endDate: endOfWeek,
          sectorTotal: sectorTotal,
          neighborhoodTotal: neighborhoodTotal,
          extraTotal: extraTotal,
          routeTotal: routeTotal,
          totalGain: totalGain,
          completedOrdersCount: completedOrdersCount,
          lastUpdated: new Date(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Erro ao atualizar o histórico semanal: ', error);
    }
  };

  const formatDate = (date) =>
    date instanceof Date && !isNaN(date) ? date.toLocaleDateString('pt-BR') : 'Data inválida';

  const formatCurrency = (value) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
        <AntDesign name="arrowleft" size={24} color="white" />
      </TouchableOpacity>

      <Text style={styles.title}>Histórico Semanal de Ganhos</Text>
      <FlatList
        data={weeklyHistory}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View style={styles.historyItem}>
            <Text style={styles.historyText}>
              Semana: {formatDate(item.startDate)} a {formatDate(item.endDate)}
            </Text>
            <Text style={styles.historyText}>Pedidos Concluídos: {item.completedOrdersCount}</Text>
            <Text style={styles.historyText}>Ganho Total: R$ {formatCurrency(item.totalGain)}</Text>
            <Text style={styles.historyText}>Setor: R$ {formatCurrency(item.sectorTotal)}</Text>
            <Text style={styles.historyText}>Bairro: R$ {formatCurrency(item.neighborhoodTotal)}</Text>
            <Text style={styles.historyText}>Extra: R$ {formatCurrency(item.extraTotal)}</Text>
            <Text style={styles.historyText}>Rotas: R$ {formatCurrency(item.routeTotal)}</Text>
          </View>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#2c5364',
    paddingTop: 50,
    paddingHorizontal: 16,
  },
  backButton: {
    position: 'absolute',
    top: 50,
    left: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: 10,
    borderRadius: 50,
    zIndex: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFF',
    textAlign: 'center',
    marginBottom: 20,
  },
  historyItem: {
    backgroundColor: '#203a43',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#00FF9D',
    shadowOpacity: 0.6,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 0 },
    elevation: 5,
    borderWidth: 2,
    borderColor: '#00FF9D',
  },
  historyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 8,
    textAlign: 'center',
  },
  historyText: {
    fontSize: 16,
    color: '#FFF',
    marginBottom: 5,
  },
  historyTotal: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#00FF9D',
    textAlign: 'center',
    marginVertical: 10,
  },
  gainsContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: 12,
    borderRadius: 8,
    marginTop: 10,
  },
  historyDetail: {
    fontSize: 16,
    color: '#FFF',
    marginBottom: 5,
  },
});

export default GainsHistoryScreen;
